import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import type { MrrSeriesPoint } from '@control-center/shared';

const brl = (v: number): string =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const formatDate = (d: string): string => {
  const [y, mo, day] = d.split('-');
  return `${day}/${mo}/${y?.slice(2)}`;
};

const BRAND = '#7c3aed'; // MRR
const GOOD = '#22c55e'; // ARR
const MUTED = '#a1a1aa';
const LINE = '#1f1f23';

/** Story 12.11 — série temporal de MRR + ARR (eixo duplo: escalas diferentes). */
export function MrrSeriesChart({ points }: { points: MrrSeriesPoint[] }) {
  if (points.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-line bg-panel">
        <div className="border-b border-line px-4 py-3">
          <h3 className="text-sm font-medium text-ink">Série temporal — MRR / ARR</h3>
        </div>
        <div className="py-12 text-center text-sm text-muted">
          Sem snapshots no período (cron de snapshot roda 00:30 BRT).
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">Série temporal — MRR / ARR</h3>
      </div>
      <div className="h-72 w-full p-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 12, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
            <XAxis dataKey="date" stroke={MUTED} fontSize={11} tickFormatter={formatDate} />
            <YAxis
              yAxisId="mrr"
              stroke={BRAND}
              fontSize={11}
              tickFormatter={(v) => brl(Number(v))}
              width={70}
            />
            <YAxis
              yAxisId="arr"
              orientation="right"
              stroke={GOOD}
              fontSize={11}
              tickFormatter={(v) => brl(Number(v))}
              width={70}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: BRAND, strokeOpacity: 0.2 }} />
            <Line
              yAxisId="mrr"
              type="monotone"
              dataKey="mrrTotal"
              stroke={BRAND}
              strokeWidth={2}
              dot={{ fill: BRAND, r: 2 }}
              name="MRR"
            />
            <Line
              yAxisId="arr"
              type="monotone"
              dataKey="arrTotal"
              stroke={GOOD}
              strokeWidth={2}
              dot={{ fill: GOOD, r: 2 }}
              name="ARR"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-ink">{formatDate(String(label))}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="mt-1 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted">{p.name}:</span>
          <span className="font-mono tabular-nums text-ink">{brl(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
}
